import { useCallback } from 'react';
import { Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../api/firebase/config';
import { createResult } from '../api/firebase/firestore/result/create';
import { editResult } from '../api/firebase/firestore/result/update';
import { getPattern } from '../api/firebase/firestore/pattern/read';
import { getUser } from '../api/firebase/firestore/user/read';
import { getMonthlyUsageCountByUserId } from '../api/firebase/firestore/company/read';
import { getUserMonthlyOcrLimit, getUserMonthlyOcrLimitMessage } from '../utils/userUsageLimit';
import type { ShareScope } from '../types/pattern';
import type { UserType } from '../types/user';
import {
  classifyDocumentsJson,
  type ClassificationFileInfo,
} from '../api/firebase/function/classification';
import { handleOcrTextPattern } from '../api/firebase/function/handleOcrTextPatternFunctions';
import { executeProvisionalOcrUsage } from '../utils/provisionalOcrUsage';
import { getUserErrorMessage } from '../modules/error';
import { isPdfFile, getPdfPageCount } from '../utils/pdfToImage';
import {
  fileProcessingType,
  ocrStatus,
  uploadSource,
  FREE_OCR_PATTERN_ID,
} from '../constants/enum';
import {
  type UploadParams,
  type UploadTask,
  type StorageUploadResult,
  uploadTaskStatus,
} from '../types/upload';

type OnProgress = (update: Partial<UploadTask>) => void;

/**
 * アップロード実行ロジックを提供するフック
 * UploadContext から利用される
 */
export const useUploadExecutor = (): {
  executeUpload: (params: UploadParams, onProgress: OnProgress) => Promise<void>;
} => {
  const executeUpload = useCallback(
    async (params: UploadParams, onProgress: OnProgress): Promise<void> => {
      const createdResultIds: string[] = [];

      try {
        // 0. 個人別 月間OCR上限チェック（#2804 Phase4）
        // 代理モードは対象外。上限未設定（無制限）の場合はスキップ。
        // ストレージアップロード・レコード作成・クレジット消費の前に停止する。
        if (!params.isProxyMode && params.companyId) {
          const execUser = await getUser(params.userId);
          const monthlyLimit = getUserMonthlyOcrLimit((execUser as UserType | undefined) ?? null);
          if (monthlyLimit !== null) {
            const usedPages = await getMonthlyUsageCountByUserId(params.companyId, params.userId);
            const limitMessage = getUserMonthlyOcrLimitMessage(
              monthlyLimit,
              usedPages,
              params.totalPageCount,
            );
            if (limitMessage) {
              throw new Error(limitMessage);
            }
          }
        }

        // 1. Storage アップロード（並列）
        onProgress({ status: uploadTaskStatus.uploading, uploadedCount: 0 });

        const storagePath =
          params.type === 'classification'
            ? `users/${params.userId}/classification`
            : `results/${params.userId}/${params.patternId}`;

        let completedCount = 0;
        const uploadResults: StorageUploadResult[] = await Promise.all(
          params.originalFiles.map(async (file, index) => {
            const fileName = file.name;
            const timestamp = Date.now();
            const filePath = `${storagePath}/${timestamp}_${index}_${fileName}`;
            const storageRef = ref(storage, filePath);

            await uploadBytes(storageRef, file);
            const downloadUrl = await getDownloadURL(storageRef);
            const pageCount = isPdfFile(file) ? await getPdfPageCount(file) : 1;

            completedCount += 1;
            onProgress({ uploadedCount: completedCount });

            return {
              fileName,
              filePath,
              downloadUrl,
              fileSize: file.size,
              fileType: file.type,
              pageCount,
            };
          }),
        );

        // 2. result レコード作成
        onProgress({ status: uploadTaskStatus.creating });

        const displayUserName = params.isProxyMode ? '事務局' : params.userName;
        const displayUserIcon = params.isProxyMode ? null : params.userIconUrl;
        const batchCreatedAt = Timestamp.now();

        // 共有範囲の継承（#2804 Phase3）: 実行に使ったパターンの共有範囲を結果へスナップショットコピー。
        // 自動仕分け経路は実行時点でパターンが未確定のため対象外（Functions側で付与）。
        let inheritedShareScope: ShareScope | undefined;
        let inheritedSharedGroupIds: string[] | undefined;
        let inheritedSharedUserIds: string[] | undefined; // 限定共有の担当者指定（#2963）
        let inheritedOwnerUserId: string | undefined;
        // フリーOCR（FREE_OCR_PATTERN_ID）は実在しない仮想パターンのため getPattern を呼ばない（#2859）。
        // 一般ユーザーが存在しない patterns/free_ocr を get するとルール評価が破綻し permission-denied になる。
        // フリーOCRは共有範囲を持たないため、継承処理自体が不要。
        if (
          params.type === 'pattern' &&
          params.patternId &&
          params.patternId !== FREE_OCR_PATTERN_ID
        ) {
          const pattern = await getPattern(params.patternId);
          if (pattern) {
            inheritedShareScope = pattern.share_scope;
            inheritedSharedGroupIds = pattern.shared_group_ids;
            inheritedSharedUserIds = pattern.shared_user_ids;
            // owner_user_id 欠損時は作成者を代替（既存パターン後方互換）
            inheritedOwnerUserId = pattern.owner_user_id ?? pattern.created_by;
          }
        }

        const resultCreatePromises = uploadResults.flatMap((upload, fileIndex) => {
          const resultCount =
            params.processingType === fileProcessingType.pagePerResult ? upload.pageCount : 1;

          const baseName = upload.fileName.replace(/\.[^/.]+$/, '');
          const ext = upload.fileName.includes('.')
            ? upload.fileName.substring(upload.fileName.lastIndexOf('.'))
            : '';

          return Array.from({ length: resultCount }, (_, pageIndex) => {
            const displayFileName =
              params.processingType === fileProcessingType.pagePerResult && upload.pageCount > 1
                ? `${baseName}_${pageIndex + 1}${ext}`
                : upload.fileName;

            const fileInfo = {
              file_name: displayFileName,
              file_path: upload.filePath,
              file_size: upload.fileSize,
              file_type: upload.fileType,
              file_url: upload.downloadUrl,
            };

            return createResult({
              patternId: params.type === 'pattern' ? params.patternId : '',
              // アップロード経路（#2967）。ホームからの自動仕分けか、パターン別画面か
              uploadSource:
                params.type === 'pattern' ? uploadSource.pattern : uploadSource.classification,
              userId: params.userId,
              userName: displayUserName,
              userIconUrl: displayUserIcon,
              files: [fileInfo],
              originalFiles: [fileInfo],
              geminiModel: params.geminiModel,
              companyId: params.companyId,
              allowAiLearning: params.allowAiLearning,
              fileIndex,
              pageNumber: pageIndex + 1,
              createdAt: batchCreatedAt,
              isProxy: params.isProxyMode,
              operatorId: params.isProxyMode ? params.operatorId : undefined,
              ...(params.type === 'pattern' && params.operatorRole !== undefined
                ? { operatorRole: params.operatorRole }
                : {}),
              // 担当者（#2642・#3061）: パターンOCR・自動仕分けの両経路で明示指定する。
              // 未指定(undefined)にすると createResult が作成者(userId)にフォールバックし、
              // 代理モードでは userId=事務局UID となって一覧で「退会済ユーザー」表示になるため（#3061）
              assigneeId: params.assigneeId,
              assigneeName: params.assigneeName,
              assigneeIconUrl: params.assigneeIconUrl,
              // 共有範囲の継承（#2804 Phase3）: パターンOCR経路のみ
              ...(params.type === 'pattern'
                ? {
                    shareScope: inheritedShareScope,
                    sharedGroupIds: inheritedSharedGroupIds,
                    sharedUserIds: inheritedSharedUserIds,
                    ownerUserId: inheritedOwnerUserId,
                  }
                : {}),
            });
          });
        });

        const resultCreateResults = await Promise.all(resultCreatePromises);
        resultCreateResults.forEach(result => {
          if (result?.resultId) {
            createdResultIds.push(result.resultId);
          }
        });

        // 3. OCR クレジット仮引き
        const totalPages = uploadResults.reduce((sum, u) => sum + u.pageCount, 0);
        let feProvisionalCreditAmount = 0;
        // 仮加算（current_month_ocr_cost）に +15円 が乗ったか。
        // 仕分け経路は仕分け前のため常に false（後段でパターン確定後に計上）。
        // この値をタスク body に乗せ、BE の確定 delta 計算で「仮加算側に +15円 が含まれていたか」の判定に使う。
        let feProvisionalHasMasterMapping = false;
        // クレジット仮引きで超過側として扱った結果数（BE のクレジット消費対象タスク数逆算に使う / #2941）
        let feProvisionalExcessResultCount = 0;
        // クレジット仮引きで超過側として扱ったページ数（BE singleResult の超過ページ判定に使う / #2942）
        let feProvisionalExcessPages = 0;
        if (params.company?.company_id) {
          // pattern タイプのみマスタ照合課金が発生する (classification は仕分け前のため後段で計上)
          // enable_master による課金ゲートは executeProvisionalOcrUsage 側で一元的に行う。
          const hasMasterMapping = params.type === 'pattern' && params.hasMasterMapping === true;
          // 各結果のページ数（結果順＝createdResultIds 順）。
          // pagePerResult: 1ファイル=pageCount 結果（各1ページ）/ singleResult: 1ファイル=1結果（pageCount ページ）。
          // 部分超過時に「ページ順に枠を埋めて超過側に入る結果数」を正確に数えるために渡す（#2942）。
          const resultPageCounts =
            params.processingType === fileProcessingType.pagePerResult
              ? uploadResults.flatMap(u => Array.from({ length: u.pageCount }, () => 1))
              : uploadResults.map(u => u.pageCount);
          const result = await executeProvisionalOcrUsage(
            params.company,
            totalPages,
            params.baseRemainingCount,
            params.isProxyMode,
            params.geminiModel,
            hasMasterMapping,
            // 結果数（マスタ照合 +15円 は1結果あたり）。
            // pagePerResult は 1ページ=1結果、singleResult は 1ファイル=1結果 のため、
            // 実際に作成した result 数を使う（ファイル数だと pagePerResult で過少になる / #2941）。
            createdResultIds.length,
            resultPageCounts,
            // 月間枠の残り円（floorしない / #2948）。未指定時は baseRemainingCount×単価 にフォールバック。
            params.baseRemainingJpy ?? null,
          );
          feProvisionalCreditAmount = result.feProvisionalCreditAmount;
          feProvisionalHasMasterMapping = result.provisionalHasMasterMapping;
          feProvisionalExcessResultCount = result.feProvisionalExcessResultCount;
          feProvisionalExcessPages = result.feProvisionalExcessPages;
        }

        // 4. API 呼び出し
        onProgress({ status: uploadTaskStatus.calling });

        if (params.type === 'classification') {
          const filePaths: ClassificationFileInfo[] = uploadResults.map(u => ({
            filePath: u.filePath,
            fileName: u.fileName,
            fileSize: u.fileSize,
            fileType: u.fileType,
            pageCount: u.pageCount,
          }));

          classifyDocumentsJson({
            filePaths,
            resultIds: createdResultIds,
            processingType: params.processingType,
            geminiModel: params.geminiModel,
            isProxy: params.isProxyMode,
            operatorId: params.isProxyMode ? params.operatorId : undefined,
            proxyCompanyId: params.isProxyMode ? params.companyId : undefined,
            provisionalDeducted: true,
            feProvisionalCreditAmount,
            provisionalHasMasterMapping: feProvisionalHasMasterMapping,
            // 超過ページ数の真値をBEへ渡す（#3052 リグレッション修正 / PR#3065）。
            // BE側で額を単価で逆算（round）すると単価未満の端数円が消失するため、
            // パターン経路と同様にFEが保持する真値を渡して逆算を廃止する。
            provisionalExcessPages: feProvisionalExcessPages,
          });
        } else {
          handleOcrTextPattern({
            filePaths: uploadResults.map(u => ({
              filePath: u.filePath,
              fileName: u.fileName,
              fileSize: u.fileSize,
              fileType: u.fileType,
              pageCount: u.pageCount,
            })),
            patternId: params.patternId,
            itemList: params.itemList,
            resultIds: createdResultIds,
            processingType: params.processingType,
            geminiModel: params.geminiModel,
            thinkingBudget: params.thinkingBudget,
            thinkingLevel: params.thinkingLevel,
            ocrEngine: params.ocrEngine,
            geminiTemperature: params.geminiTemperature,
            geminiTopP: params.geminiTopP,
            includeCoordinates: params.includeCoordinates,
            structuredEngine: params.structuredEngine,
            isProxy: params.isProxyMode,
            operatorId: params.isProxyMode ? params.operatorId : undefined,
            operatorRole: params.isProxyMode ? params.operatorRole : undefined,
            proxyCompanyId: params.isProxyMode ? params.companyId : undefined,
            provisionalDeducted: true,
            feProvisionalCreditAmount,
            provisionalHasMasterMapping: feProvisionalHasMasterMapping,
            provisionalExcessResultCount: feProvisionalExcessResultCount,
            provisionalExcessPages: feProvisionalExcessPages,
          });
        }

        // OCR処理中ステータスに変更（Firestoreリスナーで完了を監視）
        onProgress({
          status: uploadTaskStatus.processing,
          resultIds: createdResultIds,
          totalResultCount: createdResultIds.length,
          processedCount: 0,
        });
      } catch (error) {
        // pattern タイプの場合、作成済み result レコードをエラー状態に更新
        if (params.type === 'pattern' && createdResultIds.length > 0) {
          for (const resultId of createdResultIds) {
            try {
              await editResult({ resultId, data: { ocr_status: ocrStatus.error } });
            } catch {
              // エラー処理の失敗は無視
            }
          }

          // 完了コールバックでデータリフレッシュ
          if (params.onCompleted) {
            try {
              await params.onCompleted();
            } catch {
              // コンポーネントがアンマウント済みの場合は無視
            }
          }
        }

        const errorMessage = getUserErrorMessage(error, 'アップロードに失敗しました');
        onProgress({ status: uploadTaskStatus.error, errorMessage });
      }
    },
    [],
  );

  return { executeUpload };
};
